// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {Switch} from '@app/features/ui/components/form/Switch';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import Notification, {TTSNotificationMode} from '@app/features/ui/state/Notification';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/TextToSpeech.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {InfoIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
export const TextToSpeech = observer(() => {
	const {t} = useLingui();
	const handleToggleTtsCommand = useCallback((value: boolean) => {
		AccessibilityCommands.update({enableTTSCommand: value});
	}, []);
	const handleAccessibilityLinkClick = useCallback(() => {
		ComponentDispatch.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'accessibility', section: 'tts'});
	}, []);
	const ttsNotificationMode = Notification.getTTSNotificationMode();
	const ttsNotificationOptions: Array<RadioOption<TTSNotificationMode>> = [
		{
			value: TTSNotificationMode.FOR_ALL_CHANNELS,
			name: t`Every Channel`,
			desc: t`Let every incoming message be spoken, regardless of which channel is open.`,
		},
		{
			value: TTSNotificationMode.FOR_CURRENT_CHANNEL,
			name: t`Active Channel Only`,
			desc: t`Narrate messages only in whichever channel you're currently viewing; switching channels moves narration with you.`,
		},
		{
			value: TTSNotificationMode.NEVER,
			name: t`Never Automatically`,
			desc: t`Remain silent unless someone runs /tts manually.`,
		},
	];
	const handleTtsNotificationChange = useCallback((value: TTSNotificationMode) => {
		Notification.setTTSNotificationMode(value);
	}, []);
	return (
		<div className={styles.container}>
			<Switch
				label={t`Enable /tts Speech Playback`}
				description={t`Let /tts read your message aloud; disabling the setting keeps those commands as regular text.`}
				value={Accessibility.enableTTSCommand}
				onChange={handleToggleTtsCommand}
			/>
			<div className={styles.helperCallout}>
				<InfoIcon size={16} weight="fill" className={styles.helperIcon} />
				<p className={styles.helperText}>
					<Trans>
						Adjust playback speed in{' '}
						<button type="button" className={styles.linkButton} onClick={handleAccessibilityLinkClick}>
							Accessibility
						</button>
						.
					</Trans>
				</p>
			</div>
			<div className={styles.narrationSection}>
				<div className={styles.narrationHeader}>
					<h3 className={styles.narrationTitle}>{t`Automatic message narration`}</h3>
					<p className={styles.narrationDescription}>
						{t`Converts incoming content to speech, regardless of whether it came from /tts.`}
					</p>
				</div>
				<RadioGroup
					options={ttsNotificationOptions}
					value={ttsNotificationMode}
					onChange={handleTtsNotificationChange}
					aria-label={t`Speak all messages out loud`}
				/>
			</div>
		</div>
	);
});
